/**
 * The opening screen.
 *
 * No pitch. Someone who has opened this already knows what it is, and a
 * paragraph explaining the product to its own user is wasted space. What
 * belongs here is a way in — and the questions worth asking depend entirely on
 * what this reader has already studied, so they come from the server rather
 * than from a list written months ago.
 */

import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { api } from "../api.ts";
import {
  GROUP_LABELS,
  GROUP_ORDER,
  type Suggestion,
  type SuggestionGroup,
} from "../../shared/suggestions.ts";
import { Button } from "./ui/button.tsx";
import { MathText } from "../markdown/MathText.tsx";
import { Separator } from "./ui/separator.tsx";
import { Skeleton } from "./ui/skeleton.tsx";

export function EmptyState({ onPick }: { onPick: (prompt: string) => void }) {
  const { suggestions, loading } = useSuggestions();

  return (
    <div className="py-8 sm:py-12">
      <h1 className="mb-6 text-[clamp(1.6rem,1.2rem+2vw,1.9rem)] tracking-[0.01em]">
        sZero
      </h1>

      {loading ? (
        <SuggestionsSkeleton />
      ) : (
        GROUP_ORDER.map((group) => {
          const inGroup = suggestions.filter((s) => s.group === group);
          if (inGroup.length === 0) return null;

          return (
            <section key={group} className="mb-6">
              <h2 className="mb-2 font-sans text-xs font-semibold tracking-[0.08em] text-muted-foreground uppercase">
                {GROUP_LABELS[group as SuggestionGroup]}
              </h2>
              <ul className="grid gap-2">
                {inGroup.map((suggestion) => (
                  /* A grid item's automatic minimum is its content's
                     min-content width, which on a phone is wider than the
                     column — the track then overflows the stage rather than
                     the label wrapping inside it. */
                  <li key={suggestion.label} className="min-w-0">
                    <Button
                      variant="outline"
                      onClick={() => onPick(suggestion.prompt)}
                      className="group/suggestion h-auto w-full justify-between gap-3 px-4 py-3 text-left font-serif text-base font-normal whitespace-normal text-secondary-foreground hover:border-primary hover:text-foreground"
                    >
                      <MathText
                        className="min-w-0 wrap-anywhere"
                        children={suggestion.label}
                      />
                      <ArrowRight className="shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/suggestion:opacity-100" />
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          );
        })
      )}

      <Separator className="mb-4" />
      <p className="font-sans text-[0.82rem] text-muted-foreground">
        Ask for a <strong className="font-semibold">Lecture</strong> when you
        want the whole topic built up, and say{" "}
        <em>&ldquo;don&rsquo;t solve it yet&rdquo;</em> when you want to try
        first.
      </p>
    </div>
  );
}

/**
 * Suggestions are generated from study history, so they arrive a beat after the
 * screen does. Loading state rather than a flash of placeholder questions that
 * then rearrange under the cursor.
 */
function useSuggestions(): {
  suggestions: readonly Suggestion[];
  loading: boolean;
} {
  const [suggestions, setSuggestions] = useState<readonly Suggestion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    void api
      .suggestions()
      .then((payload) => {
        if (!cancelled) setSuggestions(payload.suggestions);
      })
      .catch(() => {
        // The server falls back to seeds itself; a network failure here just
        // means no suggestions, which is survivable — the composer still works.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { suggestions, loading };
}

/**
 * The bars are decorative and stay hidden from assistive tech, but the *fact*
 * that something is loading must not be — otherwise a screen reader meets an
 * empty page and no explanation for it.
 */
function SuggestionsSkeleton() {
  return (
    <div className="mb-6 grid gap-6" role="status">
      <span className="sr-only">Finding questions worth asking…</span>
      {[0, 1].map((section) => (
        <div key={section} className="grid gap-2" aria-hidden="true">
          <Skeleton className="h-3 w-40" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ))}
    </div>
  );
}
